import { type TBadgeColor } from "@components/Badge/Badge";
import { type BankName } from "@services/budget/types";

export interface IPOARecommendationsRequest {
  jointBillServiceId: number;
}

export interface IPOAStatementConsentsBanksResponse {
  banks: {
    bankId: number;
    bankName: BankName;
  }[];
}

export interface IPOAStatementConsentsStateResponse {
  activeConsentsExist: boolean;
}

export interface IPOAStatementConsentsBankLinkRequest {
  bankId: number;
}

export interface IPOAStatementConsentsBankLinkResponse {
  powerOfAttorneyLedgerConsentId: string;
  redirectUrl: string;
}

export interface IPOAStatementConsentsStatusRequest {
  powerOfAttorneyLedgerConsentId: string;
}

export enum POAStatementConsentStateId {
  wait = "wait",
  success = "success",
  failed = "failed",
}

export interface IPOAStatementConsentsStatusResponse {
  powerOfAttorneyStatementConsentStateId: POAStatementConsentStateId;
  ledgerConsentId: number;
}

export type TgetPOAStatementConsentsStatus = Promise<
  | {
      succeeded: true;
      powerOfAttorneyStatementConsentStateId: POAStatementConsentStateId;
      ledgerConsentId: number;
    }
  | {
      succeeded: false;
    }
>;

export interface IPOAStatementConsentsRecommendationsRequest {
  powerOfAttorneyLedgerConsentId: string;
  jointBillServiceId: number;
}

export interface IPOARecommendationsResponse {
  powerOfAttorneyProviders: {
    serviceProviderId: number;
    serviceProviderName: string;
  }[];
}

export enum PoaSpRequestState {
  IN_PROGRESS = "requestInProgress",
  COMPLETED = "completed",
  REJECTED = "rejected",
  CLOSED = "closed",
}

export const POAConsentStatusBadgeColorMap: Record<
  PoaSpRequestState,
  TBadgeColor
> = {
  [PoaSpRequestState.IN_PROGRESS]: "orange",
  [PoaSpRequestState.COMPLETED]: "green",
  [PoaSpRequestState.REJECTED]: "red",
  [PoaSpRequestState.CLOSED]: "grey",
};

export interface IPoaSpRequest {
  serviceProviderName: string;
  serviceProviderRequestStateId: PoaSpRequestState;
}

export interface IPoaJbsData {
  jointBillServiceId: number;
  address: string;
  serviceProviderRequests: IPoaSpRequest[];
}

export interface ISubmitPOAServiceProviderRequestsRequest {
  requestedServiceProviders: {
    jointBillServiceId: number;
    serviceProviderId?: number;
    companyCode?: string;
    serviceProviderName: string;
  }[];
}

export interface IPOASignedDocumentMetadata {
  dateOfSigning: string;
  validTo: string;
  isDocumentValid: boolean;
}

export type TgetPOASignedDocumentMetadata = Promise<
  | {
      succeeded: true;
      dateOfSigning: string;
      validTo: string;
      isDocumentValid: boolean;
    }
  | {
      succeeded: false;
    }
>;

export interface IPOARikServiceProviderRequest {
  serviceProviderSearchWord: string;
}

export interface IPOARikServiceProviderResponse {
  serviceProviders: {
    serviceProviderId?: number;
    serviceProviderName: string;
    companyCode: string;
  }[];
}

export type TgetPOASignedDocument = Promise<
  | {
      succeeded: true;
      content: string;
      fileName: string;
    }
  | {
      succeeded: false;
    }
>;

export interface IPOASignedDocumentResponse {
  content: string;
  fileName: string;
}

export interface IPOADokobitConsentRequest {
  firstName: string;
  lastName: string;
  personalCode: string;
  rulesAccepted: boolean;
}

export interface IPOADokobitConsentResponse {
  signingToken: string;
  iframeUrl: string;
}

export interface IPOADokobitConfirmRequest {
  signingToken: string;
  requestedServiceProviders: {
    jointBillServiceId: number;
    serviceProviderId?: number;
    companyCode?: string;
    serviceProviderName: string;
  }[];
}

export interface IUnderpermissionedExistingConsents {
  banks: {
    bankId: number;
    bankName: BankName;
    consents: {
      ledgerConsentId: number;
      validUntil: string;
      bankAccounts: {
        iban: string;
      }[];
    }[];
  }[];
}

export interface ISubmitUnderpermissionedExistingConsentRequest {
  ledgerConsentId: number;
}

export interface ISubmitUnderpermissionedExistingConsentResponse {
  powerOfAttorneyLedgerConsentId: string;
}
